import { Flex, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Spinner from 'components/Spinner';
import { getAllInstrumentOutputs, getDashboardBlocks } from 'redux/DashboardSliceV2';
import { getDataTypeBasedValuewithNewValue } from 'utils/FormatValues';

interface Properties {
  widgetData: any;
  isDashboardLayout?: boolean;
}

const KpiCard = ({ widgetData, isDashboardLayout }: Properties) => {
  const modalBlocks = useSelector(getDashboardBlocks);
  const [isLoading, setIsLoading] = useState(false);
  const [totalValues, setTotalValues] = useState<number | null>(null);
  const allInstrumentsOutputs = useSelector(getAllInstrumentOutputs);

  const dataType = useMemo(() => {
    try {
      const foundBlock: any = modalBlocks.find((block: any) => {
        return block.block_id === widgetData.instrument_indicators[0].block_id;
      });

      if (foundBlock?.indicators) {
        const indicator = foundBlock?.indicators.find((ind: any) => {
          return widgetData?.instrument_indicators[0]?.indicator_id === ind.indicator_id;
        });

        return {
          // The format type (e.g. "number", "percentage", "ratio", "currency") controls
          // how the value is rendered — ratio/percentage get the % path in the formatter.
          data_format: indicator?.data_format ?? '',
          // The format properties drive decimal places, scale, measure prefix/suffix, etc.
          formatProperties: indicator?.data_format_properties ?? {},
        };
      }
    } catch {
      return null;
    }
    return null;
  }, [widgetData?.instrument_indicators?.length, modalBlocks]);

  useEffect(() => {
    const extractKpiValue = (dataRows: any[] | undefined): number | null => {
      if (!dataRows || dataRows.length === 0) return null;
      const row = dataRows[0];
      // The modelAPI no-Time pivot produces {"values": "Values", "IndicatorName": 1234}
      // so we look for the first numeric value rather than blindly taking the first key.
      const foundKey = Object.keys(row).find((key) => typeof row[key] === 'number');
      return foundKey !== undefined ? (row[foundKey] as number) : null;
    };

    if (widgetData?.id && allInstrumentsOutputs && isDashboardLayout) {
      setIsLoading(false);
      const KPIdata = allInstrumentsOutputs?.[widgetData?.id];
      const value = extractKpiValue(KPIdata?.data);
      setTotalValues(value);
    } else {
      setIsLoading(false);
      const value = extractKpiValue(widgetData?.data);
      setTotalValues(value);
    }
  }, [widgetData, allInstrumentsOutputs]);

  return (
    <Flex
      height={'100%'}
      borderRadius='15px'
      flexDirection='column'
      justifyContent='center'
      className='dashboard-drag-header'
      cursor='grab'
      px={4}
      pb={2}
    >
      {isLoading ? (
        <Flex justifyContent='center' alignItems='center' flex={1}>
          <Spinner size='xl' />
        </Flex>
      ) : (
        <Tooltip
          label={
            totalValues === null || totalValues === undefined || totalValues === 0
              ? 0
              : dataType
              ? getDataTypeBasedValuewithNewValue(
                  dataType.data_format,
                  totalValues,
                  dataType.formatProperties,
                )
              : totalValues
          }
        >
          <Text
            minWidth={0}
            w={'100%'}
            fontSize={{ base: '26px', sm: '26px', md: '28px', lg: '34px', xl: '40px' }}
            fontWeight='400'
            color={'#57519C'}
            className='text-overflow'
          >
            {totalValues === null || totalValues === undefined || totalValues === 0
              ? 0
              : dataType
              ? getDataTypeBasedValuewithNewValue(
                  dataType.data_format,
                  totalValues,
                  dataType.formatProperties,
                )
              : totalValues}
          </Text>
        </Tooltip>
      )}
    </Flex>
  );
};
export default KpiCard;
